import React, { useContext, useState } from "react";
import {
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  Avatar,
  makeStyles,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import { format, isSameDay } from "date-fns";
import { AuthContext } from "../../context/Auth/AuthContext";
import useOnlineUsers from "../../hooks/useOnlineUsers";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddCommentOutlinedIcon from "@material-ui/icons/AddCommentOutlined";
import SearchIcon from "@material-ui/icons/Search";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";

import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import { toast } from "react-toastify";
import { getTokens } from "../../styles/tokens";
import { getWa } from "./chatTheme";

const useStyles = makeStyles((theme) => {
  const tk = getTokens(theme.palette.type, theme.palette.primary.main);
  const wa = getWa(theme.palette.type === "dark");
  const ellipsis = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };
  return {
    mainContainer: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      flex: 1,
      minHeight: 0,
      overflow: "hidden",
      borderRadius: 0,
      backgroundColor: tk.paper,
    },
    searchBar: {
      flex: "none",
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "0 16px 8px",
      backgroundColor: tk.paper,
    },
    // busca cinza em pílula, como a do painel de conversas do atendimento
    searchBox: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      gap: 7,
      height: 38,
      padding: "0 14px",
      borderRadius: 19,
      backgroundColor: wa.strip,
      border: "1px solid transparent",
      transition: "border-color 150ms ease, background-color 150ms ease",
      "&:focus-within": {
        backgroundColor: tk.paper,
        borderColor: wa.accent,
        boxShadow: `0 0 0 1px ${wa.accent}`,
      },
    },
    searchIcon: {
      flex: "none",
      fontSize: 18,
      color: tk.textMuted,
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      color: tk.text,
      "& input::placeholder": { color: tk.textMuted, opacity: 1 },
    },
    headerBtn: {
      flex: "none",
      width: 36,
      height: 36,
      padding: 0,
      borderRadius: "50%",
      color: wa.chipText,
      "& svg": { fontSize: 21 },
      "&:hover": { backgroundColor: wa.chip },
    },
    toolbarSlot: {
      flex: "none",
    },
    chatList: {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      backgroundColor: tk.paper,
      ...theme.scrollbarStyles,
      "& .MuiList-root": { padding: 0 },
    },
    // linhas planas, como a lista de conversas do atendimento
    listItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      minHeight: 72,
      padding: "11px 14px 10px",
      borderBottom: `1px solid ${tk.line}`,
      borderRadius: 0,
      backgroundColor: "transparent",
      cursor: "pointer",
      "&:hover": { backgroundColor: wa.hover },
      "&:hover $moreButton, &:focus-within $moreButton": { opacity: 1 },
    },
    listItemActive: {
      backgroundColor: wa.selected,
      "&:hover": { backgroundColor: wa.selected },
      "& $moreButton": { opacity: 1 },
    },
    avatarWrap: {
      position: "relative",
      flex: "none",
      width: 46,
      height: 46,
    },
    avatar: {
      width: 46,
      height: 46,
    },
    presenceDot: {
      position: "absolute",
      right: 0,
      bottom: 0,
      width: 12,
      height: 12,
      borderRadius: "50%",
      border: `2px solid ${tk.paper}`,
      boxSizing: "border-box",
      backgroundColor: tk.textMuted,
    },
    presenceOnline: {
      backgroundColor: "#25d366",
    },
    body: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 3,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
      minHeight: 20,
    },
    userName: {
      flex: 1,
      minWidth: 0,
      color: tk.text,
      fontSize: 15,
      fontWeight: 600,
      lineHeight: "20px",
      ...ellipsis,
    },
    time: {
      flex: "none",
      color: tk.textMuted,
      fontSize: 11,
      lineHeight: "16px",
      fontVariantNumeric: "tabular-nums",
    },
    timeUnread: {
      color: wa.unread,
      fontWeight: 700,
    },
    secondary: {
      flex: 1,
      minWidth: 0,
      color: tk.textSecondary,
      fontSize: 12.5,
      lineHeight: "17px",
      ...ellipsis,
    },
    secondaryUnread: {
      color: tk.text,
      fontWeight: 500,
    },
    unread: {
      flex: "none",
      minWidth: 20,
      height: 20,
      padding: "0 6px",
      borderRadius: 10,
      color: wa.unreadText,
      backgroundColor: wa.unread,
      fontSize: 11.5,
      fontWeight: 700,
      lineHeight: "20px",
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
      boxSizing: "border-box",
    },
    moreButton: {
      flex: "none",
      width: 24,
      height: 20,
      padding: 0,
      borderRadius: 6,
      color: tk.textMuted,
      opacity: 0,
      transition: "opacity 120ms ease",
      "&:focus-visible": { opacity: 1 },
      "& svg": { fontSize: 20 },
      "@media (hover: none)": { opacity: 1 },
    },
    emptyState: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      padding: "48px 24px",
      color: tk.textSecondary,
      fontSize: 13,
      textAlign: "center",
      "& svg": { fontSize: 38, color: tk.textMuted },
    },
    menuIcon: {
      minWidth: 32,
      color: tk.textSecondary,
      "& svg": { fontSize: 19 },
    },
    menuDanger: {
      color: tk.semantic.error,
      "& $menuIcon": { color: tk.semantic.error },
    },
    // ---------- modais (mesmo padrão do "Nova conversa" do atendimento) ----------
    dialogPaper: {
      width: "min(440px, calc(100vw - 32px))",
      maxWidth: "none",
      margin: 16,
      borderRadius: 12,
    },
    dialogHeader: {
      padding: "18px 22px 6px",
    },
    dialogTitle: {
      margin: 0,
      fontSize: 19,
      fontWeight: 700,
      lineHeight: "26px",
      letterSpacing: "-0.01em",
      color: theme.palette.text.primary,
    },
    dialogSubtitle: {
      margin: "2px 0 0",
      fontSize: 13,
      lineHeight: "18px",
      color: theme.palette.text.secondary,
    },
    dialogContent: {
      padding: "12px 22px 6px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    dialogActions: {
      padding: "12px 22px 18px",
      gap: 6,
    },
    dialogCancel: {
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
    userList: {
      maxHeight: "min(400px, 50vh)",
      margin: "0 -8px",
      padding: 0,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    userItem: {
      gap: 12,
      padding: "8px",
      borderRadius: tk.radius.sm,
    },
    userAvatar: {
      width: 40,
      height: 40,
    },
    userText: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    userTitle: {
      color: tk.text,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: "20px",
      ...ellipsis,
    },
    userSubtitle: {
      color: tk.textSecondary,
      fontSize: 12.5,
      lineHeight: "17px",
      ...ellipsis,
    },
    centered: {
      display: "flex",
      justifyContent: "center",
      padding: 20,
      color: tk.textSecondary,
      fontSize: 13,
      textAlign: "center",
    },
    detailsHead: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 4,
      padding: "8px 0 10px",
      textAlign: "center",
    },
    detailsAvatar: {
      width: 96,
      height: 96,
      marginBottom: 6,
      fontSize: 34,
    },
    detailsTitle: {
      color: tk.text,
      fontSize: 18,
      fontWeight: 600,
      overflowWrap: "anywhere",
    },
    detailsDescription: {
      color: tk.textSecondary,
      fontSize: 13,
      overflowWrap: "anywhere",
    },
    sectionLabel: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
    },
  };
});

// hora (hoje) ou data curta, como na lista de conversas do atendimento
const shortTime = (value) => {
  const date = new Date(value);
  if (!value || isNaN(date.getTime())) return "";
  return isSameDay(date, new Date())
    ? format(date, "HH:mm")
    : format(date, "dd/MM/yyyy");
};

const userImage = (user) => {
  if (!user || !user.profileImage) return undefined;
  return user.profileImage.startsWith("http")
    ? user.profileImage
    : `${getBackendUrl()}/public/company${user.companyId}/user/${user.profileImage}`;
};

const groupImage = (chat) => {
  if (!chat || !chat.groupImage) return undefined;
  return chat.groupImage.startsWith("http")
    ? chat.groupImage
    : `${getBackendUrl()}${chat.groupImage}`;
};

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  pageInfo,
  loading,
  findChats,
  toolbar,
  showingGroups,
}) {
  const classes = useStyles();
  const history = useHistory();
  const { user: loggedInUser } = useContext(AuthContext);
  const onlineUsers = useOnlineUsers();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [createChatModalOpen, setCreateChatModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");

  const { id } = useParams();

  // Ordena os chats pelo campo updatedAt antes de filtrar
  const orderedChats = [...chats].sort(
    (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
  );

  const filteredChats = orderedChats.filter((chat) => {
    const isGroup = chat.isGroup;
    const otherUser =
      !isGroup && chat.users.find((u) => u.userId !== loggedInUser.id);
    const name = isGroup ? chat.title : otherUser?.user?.name || "";
    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const isShowingOnlyGroups = filteredChats.every((chat) => chat.isGroup);

  const goToMessages = async (chat) => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, { userId: loggedInUser.id });
      } catch (err) {}
    }
    handleSelectChat(chat);
    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
    handleClose();
  };

  const unreadMessages = (chat) => {
    const currentUser = chat.users.find((u) => u.userId === loggedInUser.id);
    return currentUser.unreads;
  };

  const getSecondaryText = (chat) => {
    // Prioriza o campo lastMessage retornado do backend
    let msg = "";
    if (chat.lastMessage && typeof chat.lastMessage === "object") {
      if (chat.lastMessage.isDeleted) {
        msg = i18n.t("chatMessages.deleted");
      } else if (chat.lastMessage.mediaType === "audio") {
        msg = "[Áudio]";
      } else if (chat.lastMessage.mediaType === "image") {
        msg = "[Imagem]";
      } else if (chat.lastMessage.mediaType === "video") {
        msg = "[Vídeo]";
      } else if (chat.lastMessage.mediaType && chat.lastMessage.mediaName) {
        msg = `[Arquivo] ${chat.lastMessage.mediaName}`;
      } else if (chat.lastMessage.message) {
        msg = chat.lastMessage.message;
      }
    } else if (typeof chat.lastMessage === "string") {
      msg = chat.lastMessage;
    } else if (chat.messages && chat.messages.length > 0) {
      const lastMsg = chat.messages[chat.messages.length - 1];
      msg = lastMsg.message;
    } else {
      msg = i18n.t("chatList.noMessages");
    }
    // Limita o texto a 80 caracteres
    if (msg && msg.length > 40) {
      return msg.substring(0, 40) + "...";
    }
    return msg;
  };

  const handleClick = (event, chat) => {
    setAnchorEl(event.currentTarget);
    setSelectedChat(chat);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenDetails = () => {
    setDetailsModalOpen(true);
    handleClose();
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
  };

  const formatLastSeen = (date) => {
    if (!date) return "";
    const lastSeen = new Date(date);
    const now = new Date();
    const diff = now - lastSeen;
    if (diff < 60000) return i18n.t("chatList.now");
    if (diff < 3600000)
      return i18n.t("chatList.minutesAgo", { count: Math.floor(diff / 60000) });
    if (diff < 86400000)
      return i18n.t("chatList.hoursAgo", { count: Math.floor(diff / 3600000) });
    return lastSeen.toLocaleDateString();
  };

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const { data } = await api.get("/users/list");
      // Filtrar o usuário logado da lista
      const filteredUsers = data.filter((user) => user.id !== loggedInUser.id);
      setAvailableUsers(filteredUsers);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar usuários");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleOpenCreateChat = () => {
    setCreateChatModalOpen(true);
    setUserSearchTerm("");
    loadUsers();
  };

  const handleCloseCreateChat = () => {
    setCreateChatModalOpen(false);
    setUserSearchTerm("");
  };

  const handleCreateChatWithUser = async (selectedUser) => {
    try {
      // Verificar se já existe um chat com este usuário
      const existingChat = chats.find((c) => {
        const hasSelectedUser = c.users.some(
          (u) => u.userId === selectedUser.id
        );
        const hasLoggedInUser = c.users.some(
          (u) => u.userId === loggedInUser.id
        );
        const isIndividualChat = !c.isGroup && c.users.length === 2;

        return isIndividualChat && hasSelectedUser && hasLoggedInUser;
      });

      if (existingChat) {
        toast.info("Chat com este usuário já existe!");
        handleSelectChat(existingChat);
        handleCloseCreateChat();
        return;
      }

      // Criar novo chat
      const { data } = await api.post("/chats", {
        users: [{ id: selectedUser.id }],
        isGroup: false,
      });

      toast.success("Chat criado com sucesso!");

      // Atualizar a lista de chats
      if (findChats) {
        await findChats();
      }

      handleSelectChat(data);
      handleCloseCreateChat();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao criar chat");
    }
  };

  const filteredAvailableUsers = availableUsers.filter((user) =>
    user.name.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // quem chama sabe em qual aba está; sem isso, cai na inferência antiga pela lista
  const canCreateChat =
    showingGroups === undefined ? !isShowingOnlyGroups : !showingGroups;

  return (
    <>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {selectedChat.ownerId === loggedInUser.id && selectedChat.isGroup && (
          <MenuItem
            onClick={() => {
              handleClose();
              handleEditChat(selectedChat);
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <EditOutlinedIcon />
            </ListItemIcon>
            {i18n.t("chatList.edit")}
          </MenuItem>
        )}
        {selectedChat.isGroup && (
          <MenuItem onClick={handleOpenDetails}>
            <ListItemIcon className={classes.menuIcon}>
              <InfoOutlinedIcon />
            </ListItemIcon>
            {i18n.t("chatList.details")}
          </MenuItem>
        )}
        {loggedInUser.profile === "admin" && (
          <MenuItem
            className={classes.menuDanger}
            onClick={() => {
              handleClose();
              setConfirmModalOpen(true);
            }}
          >
            <ListItemIcon className={classes.menuIcon}>
              <DeleteOutlineIcon />
            </ListItemIcon>
            {i18n.t("chatList.delete")}
          </MenuItem>
        )}
      </Menu>
      <ConfirmationModal
        title={i18n.t("chatList.deleteChat")}
        open={confirmationModal}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
      >
        {i18n.t("chatList.deleteChatConfirm")}
      </ConfirmationModal>
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetails}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="chat-details-title"
      >
        <div className={classes.dialogHeader}>
          <h2 id="chat-details-title" className={classes.dialogTitle}>
            {i18n.t("chatList.detailsTitle")}
          </h2>
        </div>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.detailsHead}>
            <Avatar
              src={groupImage(selectedChat)}
              className={classes.detailsAvatar}
            >
              {(selectedChat?.title || "G").charAt(0).toUpperCase()}
            </Avatar>
            <span className={classes.detailsTitle}>{selectedChat?.title}</span>
            {selectedChat?.description && (
              <span className={classes.detailsDescription}>
                {selectedChat.description}
              </span>
            )}
          </div>
          <span className={classes.sectionLabel}>
            {(selectedChat?.users?.length || 0) + " participantes"}
          </span>
          <List className={classes.userList}>
            {selectedChat?.users?.map((user) => (
              <ListItem key={user.userId} className={classes.userItem}>
                <Avatar
                  src={userImage(user.user)}
                  className={classes.userAvatar}
                >
                  {(user.user?.name || "?").charAt(0).toUpperCase()}
                </Avatar>
                <span className={classes.userText}>
                  <span className={classes.userTitle}>{user.user?.name}</span>
                  <span className={classes.userSubtitle}>
                    {user.userId === selectedChat.ownerId
                      ? i18n.t("chatList.admin")
                      : i18n.t("chatList.member")}
                  </span>
                </span>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseDetails} className={classes.dialogCancel}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
      <div className={classes.mainContainer}>
        <div className={classes.searchBar}>
          <div className={classes.searchBox}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              placeholder={i18n.t("chatList.searchChat")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              inputProps={{ "aria-label": i18n.t("chatList.searchChat") }}
            />
          </div>
          {canCreateChat && (
            <Tooltip title={i18n.t("chatList.createNewChat")}>
              <IconButton
                onClick={handleOpenCreateChat}
                className={classes.headerBtn}
                aria-label={i18n.t("chatList.createNewChat")}
              >
                <AddCommentOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
        {toolbar && <div className={classes.toolbarSlot}>{toolbar}</div>}

        <div className={classes.chatList}>
          {filteredChats.length === 0 && !loading && (
            <div className={classes.emptyState}>
              <ForumOutlinedIcon />
              <span>
                {showingGroups
                  ? i18n.t("chatIndex.noGroups")
                  : i18n.t("chatIndex.noChats")}
              </span>
            </div>
          )}
          <List>
            {filteredChats.map((chat, key) => {
              const isGroup = chat.isGroup;
              const otherUser =
                !isGroup &&
                chat.users.find((u) => u.userId !== loggedInUser.id);
              const isOnline =
                !isGroup &&
                otherUser?.user &&
                onlineUsers.some(
                  (u) => u.id === otherUser.user.id && u.online === true
                );
              const unreads = unreadMessages(chat);
              const name = isGroup
                ? chat.title || "Grupo"
                : otherUser && otherUser.user && otherUser.user.name
                ? otherUser.user.name
                : "Usuário";

              return (
                <ListItem
                  key={key}
                  button
                  onClick={() => goToMessages(chat)}
                  className={`${classes.listItem} ${
                    chat.uuid === id ? classes.listItemActive : ""
                  }`}
                >
                  <div className={classes.avatarWrap}>
                    <Avatar
                      src={isGroup ? groupImage(chat) : userImage(otherUser?.user)}
                      className={classes.avatar}
                    >
                      {isGroup
                        ? chat.title
                          ? chat.title.charAt(0).toUpperCase()
                          : "G"
                        : otherUser && otherUser.user && otherUser.user.name
                        ? otherUser.user.name.charAt(0).toUpperCase()
                        : "?"}
                    </Avatar>
                    {!isGroup && otherUser?.user && (
                      <span
                        className={`${classes.presenceDot} ${
                          isOnline ? classes.presenceOnline : ""
                        }`}
                        title={isOnline ? "Online" : "Offline"}
                      />
                    )}
                  </div>

                  <div className={classes.body}>
                    <div className={classes.row}>
                      <span className={classes.userName}>{name}</span>
                      <span
                        className={`${classes.time} ${
                          unreads > 0 ? classes.timeUnread : ""
                        }`}
                      >
                        {shortTime(chat.updatedAt)}
                      </span>
                    </div>
                    <div className={classes.row}>
                      <span
                        className={`${classes.secondary} ${
                          unreads > 0 ? classes.secondaryUnread : ""
                        }`}
                      >
                        {getSecondaryText(chat)}
                      </span>
                      {unreads > 0 && (
                        <span className={classes.unread}>{unreads}</span>
                      )}
                      {(loggedInUser.profile === "admin" ||
                        (selectedChat.ownerId === loggedInUser.id &&
                          selectedChat.isGroup)) && (
                        <IconButton
                          aria-label="Opções da conversa"
                          aria-haspopup="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClick(e, chat);
                          }}
                          className={classes.moreButton}
                        >
                          <ExpandMoreIcon />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </ListItem>
              );
            })}
          </List>
        </div>
      </div>
      <Dialog
        open={createChatModalOpen}
        onClose={handleCloseCreateChat}
        classes={{ paper: classes.dialogPaper }}
        aria-labelledby="new-chat-title"
      >
        <div className={classes.dialogHeader}>
          <h2 id="new-chat-title" className={classes.dialogTitle}>
            {i18n.t("chatList.createNewChat")}
          </h2>
          {/* TODO i18n */}
          <p className={classes.dialogSubtitle}>
            Escolha com quem da equipe você quer conversar.
          </p>
        </div>
        <DialogContent className={classes.dialogContent}>
          <TextField
            fullWidth
            autoFocus
            variant="outlined"
            size="small"
            placeholder={i18n.t("chatList.searchUser")}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon className={classes.searchIcon} />
                </InputAdornment>
              ),
            }}
          />

          {usersLoading ? (
            <div className={classes.centered}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <List className={classes.userList}>
              {filteredAvailableUsers.map((user) => (
                <ListItem
                  key={user.id}
                  button
                  onClick={() => handleCreateChatWithUser(user)}
                  className={classes.userItem}
                >
                  <div className={classes.avatarWrap} style={{ width: 40, height: 40 }}>
                    <Avatar src={userImage(user)} className={classes.userAvatar}>
                      {(user.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <span
                      className={`${classes.presenceDot} ${
                        user.online === true ? classes.presenceOnline : ""
                      }`}
                      title={user.online === true ? "Online" : "Offline"}
                    />
                  </div>
                  <span className={classes.userText}>
                    <span className={classes.userTitle}>{user.name}</span>
                    <span className={classes.userSubtitle}>{user.email}</span>
                  </span>
                </ListItem>
              ))}
              {filteredAvailableUsers.length === 0 && !usersLoading && (
                <div className={classes.centered}>
                  {userSearchTerm
                    ? i18n.t("chatList.noUserFound")
                    : i18n.t("chatList.noUserAvailable")}
                </div>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={handleCloseCreateChat}
            className={classes.dialogCancel}
          >
            {i18n.t("chatList.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
